import { useRef, useState } from "react";
import { Modal, Form, Table, Alert } from "react-bootstrap";
import * as XLSX from "xlsx";
import { PrimaryButton } from "components/Button/style";

interface ItemImportar {
  placa: string;
  idProcesso: string;
}

interface Props {
  show: boolean;
  loading: boolean;
  onFechar: () => void;
  onConfirmar: (itens: ItemImportar[]) => Promise<void>;
}

function normalizarChave(chave: string): string {
  return chave.toLowerCase().replace(/[^a-z]/g, "");
}

function extrairItens(sheet: XLSX.WorkSheet): { itens: ItemImportar[]; erro: string | null } {
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: "" });
  if (!rows.length) return { itens: [], erro: "Planilha vazia." };

  const keys = Object.keys(rows[0]);
  const keyPlaca = keys.find((k) => normalizarChave(k) === "placa");
  const keyProcesso = keys.find((k) => ["processo", "idprocesso", "idprocesso"].includes(normalizarChave(k)));

  if (!keyPlaca) return { itens: [], erro: 'Coluna "placa" não encontrada na planilha.' };
  if (!keyProcesso) return { itens: [], erro: 'Coluna "processo" ou "idProcesso" não encontrada na planilha.' };

  const itens: ItemImportar[] = rows
    .map((row) => ({
      placa: String(row[keyPlaca] ?? "").trim().toUpperCase(),
      idProcesso: String(row[keyProcesso] ?? "").trim(),
    }))
    .filter((item) => item.placa && item.idProcesso);

  if (!itens.length) return { itens: [], erro: "Nenhum item válido encontrado. Verifique se as colunas possuem dados." };

  return { itens, erro: null };
}

export function ModalImportarPlanilha({ show, loading, onFechar, onConfirmar }: Props) {
  const [itens, setItens] = useState<ItemImportar[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    setNomeArquivo(arquivo.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const { itens: parsed, erro: erroParser } = extrairItens(sheet);
        setErro(erroParser);
        setItens(parsed);
      } catch {
        setErro("Erro ao ler o arquivo. Certifique-se de que é um arquivo .xlsx ou .xls válido.");
        setItens([]);
      }
    };
    reader.readAsArrayBuffer(arquivo);
  }

  function handleFechar() {
    setItens([]);
    setErro(null);
    setNomeArquivo(null);
    if (inputRef.current) inputRef.current.value = "";
    onFechar();
  }

  async function handleConfirmar() {
    await onConfirmar(itens);
    handleFechar();
  }

  return (
    <Modal show={show} onHide={handleFechar} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>Importar Planilha e Imprimir Etiquetas</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ fontSize: "0.85rem", color: "#6c757d", marginBottom: 12 }}>
          Selecione um arquivo <strong>.xlsx</strong> com as colunas <strong>placa</strong> e <strong>processo</strong> (ou <strong>idProcesso</strong>).
          Os registros serão criados como PENDENTE e as etiquetas impressas automaticamente.
        </p>

        <Form.Group className="mb-3">
          <Form.Label>Arquivo</Form.Label>
          <Form.Control
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleArquivo}
            disabled={loading}
          />
          {nomeArquivo && <Form.Text className="text-muted">{nomeArquivo}</Form.Text>}
        </Form.Group>

        {erro && (
          <Alert variant="danger" className="py-2" style={{ fontSize: "0.85rem" }}>
            {erro}
          </Alert>
        )}

        {itens.length > 0 && (
          <>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong style={{ fontSize: "0.9rem" }}>Preview — {itens.length} item(s)</strong>
            </div>
            <div style={{ maxHeight: 300, overflowY: "auto" }}>
              <Table size="sm" bordered hover className="mb-0">
                <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                  <tr>
                    <th style={{ fontSize: "0.82rem" }}>#</th>
                    <th style={{ fontSize: "0.82rem" }}>Placa</th>
                    <th style={{ fontSize: "0.82rem" }}>ID Processo</th>
                  </tr>
                </thead>
                <tbody>
                  {itens.map((item, i) => (
                    <tr key={i}>
                      <td style={{ fontSize: "0.82rem", color: "#6c757d" }}>{i + 1}</td>
                      <td style={{ fontSize: "0.82rem", fontFamily: "monospace" }}>{item.placa}</td>
                      <td style={{ fontSize: "0.82rem", fontFamily: "monospace" }}>{item.idProcesso}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <PrimaryButton outline variante="primary" onClick={handleFechar} disabled={loading}>
          Cancelar
        </PrimaryButton>
        <PrimaryButton
          variante="primary"
          disabled={itens.length === 0 || loading}
          onClick={handleConfirmar}
        >
          {loading ? "Importando e imprimindo..." : `Confirmar e Imprimir (${itens.length})`}
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
}
